// src/pages/ResetPassword.tsx - Fixed with mobile-responsive OTP
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  ArrowLeft,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";
import { authService } from "../services/api";

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Get email from location state
  const locationEmail = location.state?.email;

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // If email is passed from forgot password page, use it
  useEffect(() => {
    if (locationEmail) {
      setEmail(locationEmail);
    }
  }, [locationEmail]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      const newOtp = [...otp];

      digits.slice(0, 6).forEach((digit, index) => {
        newOtp[index] = digit;
      });

      setOtp(newOtp);

      const lastInput = inputRefs.current[Math.min(5, digits.length - 1)];
      if (lastInput) lastInput.focus();
    } else {
      toast.error("Please paste exactly 6 digits");
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }

    if (countdown > 0) {
      toast.error(
        `Please wait ${countdown} seconds before requesting a new code`
      );
      return;
    }

    setIsResending(true);
    try {
      const response = await authService.forgotPassword(email);
      if (response.success) {
        toast.success("New OTP sent successfully!");
        setCountdown(60);
      } else {
        toast.error(response.message || "Failed to resend OTP");
      }
    } catch (error: any) {
      // Don't show "session expired" for this API call
      if (
        error?.response?.data?.error &&
        !error.response.data.error.includes("session")
      ) {
        toast.error(error.response.data.error || "Failed to resend OTP");
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join("");

    // Validation
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (otpString.length !== 6) {
      toast.error("Please enter all 6 digits of the OTP");
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      toast.error("Please enter valid digits (0-9)");
      return;
    }

    if (!password || !confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      const response = await authService.resetPassword(
        email,
        otpString,
        password,
        confirmPassword
      );

      if (response.success) {
        toast.success("Password reset successfully!");
        navigate("/login", {
          state: {
            message:
              "Password has been reset. Please login with your new password.",
          },
        });
      } else {
        toast.error(response.message || "Password reset failed");
      }
    } catch (error: any) {
      // Handle errors without showing "session expired" for password reset
      if (error.response?.data?.errors) {
        // Laravel validation errors
        Object.values(error.response.data.errors).forEach((err: any) => {
          if (!err[0].toLowerCase().includes("session")) {
            toast.error(err[0]);
          }
        });
      } else if (error.response?.data?.error) {
        if (!error.response.data.error.toLowerCase().includes("session")) {
          toast.error(error.response.data.error);
        }
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const passwordStrength = (pass: string) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (/[A-Z]/.test(pass)) strength++;
    if (/[a-z]/.test(pass)) strength++;
    if (/[0-9]/.test(pass)) strength++;
    if (/[^A-Za-z0-9]/.test(pass)) strength++;
    return strength;
  };

  const strength = passwordStrength(password);
  const strengthColors = [
    "bg-red-500",
    "bg-orange-500",
    "bg-yellow-500",
    "bg-green-400",
    "bg-green-600",
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-5">
      <div className="w-full max-w-md">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-primary hover:text-primary/80"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to login
          </Link>
        </div>

        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img src={logo} alt="ObeliskRecovery" className="h-12 w-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Reset Your Password
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Enter OTP and set new password
          </p>
        </div>

        {/* Combined Reset Form */}
        <div className={GlobalStyles.card}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className={GlobalStyles.inputLabel}>
                Email Address
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${GlobalStyles.input} pl-10 text-gray-900 dark:text-white`}
                  placeholder="you@example.com"
                  disabled={isLoading || !!locationEmail}
                />
              </div>
            </div>

            {/* OTP Inputs - Fixed for mobile */}
            <div>
              <label className={GlobalStyles.inputLabel}>
                Verification Code
              </label>
              <div
                className="flex justify-center space-x-2 sm:space-x-3 mt-2"
                onPaste={handlePaste}
              >
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    id={`otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className={`${GlobalStyles.input} w-10 h-12 sm:w-12 text-center text-xl font-semibold !p-0 text-gray-900 dark:text-white`}
                    autoFocus={index === 0}
                    disabled={isLoading}
                    autoComplete="one-time-code"
                    style={{
                      fontSize: "1.25rem",
                      WebkitAppearance: "none",
                      MozAppearance: "textfield",
                    }}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
                Enter the 6-digit code sent to your email
              </p>

              {/* Resend Code */}
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending || countdown > 0 || !email}
                  className={`text-sm text-primary hover:text-primary/80 transition-colors ${
                    !email ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {isResending ? (
                    <>
                      <div
                        className={`${GlobalStyles.spinner} w-3 h-3 mr-1 inline-block`}
                      ></div>
                      Sending...
                    </>
                  ) : countdown > 0 ? (
                    `Resend in ${countdown}s`
                  ) : (
                    "Didn't receive code? Resend"
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="password" className={GlobalStyles.inputLabel}>
                New Password
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${GlobalStyles.input} pl-10 pr-10 text-gray-900 dark:text-white`}
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Password Strength Meter */}
              {password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Password strength:
                    </span>
                    <span className="text-xs font-medium">
                      {strength === 0 && "Very Weak"}
                      {strength === 1 && "Weak"}
                      {strength === 2 && "Fair"}
                      {strength === 3 && "Good"}
                      {strength === 4 && "Strong"}
                      {strength === 5 && "Very Strong"}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${strengthColors[strength]} transition-all duration-300`}
                      style={{ width: `${(strength / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className={GlobalStyles.inputLabel}
              >
                Confirm New Password
              </label>
              <div className="relative mt-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`${
                    GlobalStyles.input
                  } pl-10 pr-10 text-gray-900 dark:text-white ${
                    confirmPassword && password !== confirmPassword
                      ? "border-red-500 dark:border-red-500"
                      : confirmPassword && password === confirmPassword
                      ? "border-green-500 dark:border-green-500"
                      : ""
                  }`}
                  placeholder="••••••••"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={isLoading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Passwords do not match
                </p>
              )}
              {confirmPassword && password === confirmPassword && (
                <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                  ✓ Passwords match
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={
                  isLoading ||
                  otp.join("").length !== 6 ||
                  password !== confirmPassword ||
                  password.length < 8 ||
                  !email.includes("@")
                }
                className={`${GlobalStyles.button} ${
                  GlobalStyles.buttonPrimary
                } ${GlobalStyles.buttonLarge} w-full ${
                  otp.join("").length !== 6 ||
                  password !== confirmPassword ||
                  password.length < 8 ||
                  !email.includes("@")
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div
                      className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                    ></div>
                    Resetting Password...
                  </div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Remember your password?
                </span>
              </div>
            </div>

            {/* Login Link */}
            <div className="mt-6">
              <Link
                to="/login"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full justify-center`}
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>

        {/* Security Tips */}
        <div className={`${GlobalStyles.alert} ${GlobalStyles.alertInfo} mt-6`}>
          <Lock className="h-5 w-5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium mb-1">Password Requirements:</p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <li className="flex items-start">
                <CheckCircle className="h-3 w-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>At least 8 characters long</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-3 w-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Mix of uppercase and lowercase letters</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-3 w-3 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Include numbers and special characters</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
